"use client"

import type React from "react"
import { useEffect } from "react"
import { motion } from "framer-motion"
import { useHeaderTheme } from "@/providers/HeaderTheme"
import type { Page } from "@/payload-types"
import { CMSLink } from "@/components/Link"
import { Media } from "@/components/Media"
import RichText from "@/components/RichText"
import { cn } from "@/utilities/cn"

export const HighImpactHero: React.FC<Page["hero"]> = ({ links, media, richText }) => {
  const { setHeaderTheme } = useHeaderTheme()

  useEffect(() => {
    setHeaderTheme("light")
  }, [setHeaderTheme])

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.5 + i * 0.2,
        ease: [0.25, 0.4, 0.25, 1] as any,
      },
    }),
  }

  return (
    <div
      id="high-impact-hero"
      className={cn(
        "relative min-h-[calc(100vh-7.5rem)] py-24 flex items-center justify-center overflow-hidden bg-[#0D3121]"
      )}
      // className={cn(
      //   "-mt-16  -mt-42 relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0D3121] pt-20"
      // )}
      data-theme="light"
    >
      <div className="relative z-10 container mx-auto px-4 md:px-6">
        <div className="max-w-160 mx-auto text-left md:text-center">
          {richText && (
            <motion.div initial="hidden" animate="visible" variants={fadeUpVariants} custom={0}>
              <RichText className="lg:[&_p]:pt-6 text-xl xl:text-2xl text-mini-dark-primary [&_p]:text-mini-dark-secondary" data={richText} enableGutter={false} />
            </motion.div>
          )}

          {Array.isArray(links) && links.length > 0 && (
            <motion.ul
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              custom={1}
              className="flex md:justify-center gap-4 mt-20"
            >
              {links.map(({ link }, i) => (
                <li key={i}>
                  <CMSLink
                    {...link}
                  />
                </li>
              ))}
            </motion.ul>
          )}
        </div>
      </div>

      {media && typeof media === "object" && (
        <div className="absolute inset-0 overflow-hidden">
          <div className="relative h-full w-full">
            <Media
              fill
              imgClassName="object-cover opacity-30"
              priority
              resource={media}
              htmlElement={null}
            />
          </div>
        </div>
      )}

      <div className="absolute inset-0 bg-linear-to-b from-minigreen-7 via-minigreen-7/40 to-minigreen-7/10 dark:from-minigreen-bg-dark dark:to-minigreen-bg-dark pointer-events-none z-0" />
    </div>
  )
}
